import { formatDisplayDateTime } from '../../utils/format';
import { StatusBadge, type TimesheetStatus } from './StatusBadge';

interface FieldChange {
  field: string;
  oldValue: unknown;
  newValue: unknown;
}

interface StateChangeItem {
  id: number;
  fromState?: string | null;
  toState?: string | null;
  state?: string | null;
  reason: string | null;
  changes?: FieldChange[] | null;
  createdAt: string | null;
  user: {
    id: number;
    email: string;
    firstName: string | null;
    lastName: string | null;
  } | null;
}

interface StateHistoryProps {
  history: StateChangeItem[];
  isLoading?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
}

function getUserName(user: StateChangeItem['user']): string {
  if (!user) return 'System';
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }
  return user.email;
}

// Status transition labels
const STATUS_ACTIONS: Record<string, string> = {
  'open-submitted': 'submitted for review',
  'submitted-approved': 'approved',
  'submitted-rejected': 'rejected',
  'rejected-submitted': 'resubmitted for review',
  'approved-open': 'reopened',
  'submitted-open': 'reopened',
  'rejected-open': 'reopened',
  'open-cancelled': 'cancelled',
  'submitted-cancelled': 'cancelled',
  'approved-cancelled': 'cancelled',
  'cancelled-open': 'reopened',
  'approved-reimbursed': 'marked as reimbursed',
  'open-sent': 'marked as sent',
  'sent-open': 'reopened',
};

// Action type labels (for non-transition states)
const ACTION_LABELS: Record<string, string> = {
  created: 'created',
  updated: 'updated',
  archived: 'archived',
  restored: 'restored',
  deleted: 'deleted',
  assignment_created: 'added assignment',
  assignment_updated: 'updated assignment',
  assignment_deleted: 'removed assignment',
  // 2026-05-18 — SLA Hour Package monthly overage audit on Project subject
  overage_approved: 'approved overage hours',
  overage_acknowledged: 'acknowledged overage',
  // External tasks v1 (260702) bod 3 — external reviewer decision on a task.
  external_approved: 'approved the task (sent to PM)',
  external_rejected: 'returned the task for fix (sent to PM)',
  // Quick task 260810-l18 — ticket author's post-completion review decision.
  author_approved: 'approved the completed ticket',
  author_reopened: 'reopened the ticket',
  // MKS-352 — the Phase 110 one-off discount an operator entered at
  // generation time, surfaced in the invoice detail's History.
  discount_override_applied: 'applied a one-off discount',
};

function getActionText(item: StateChangeItem): string {
  const state = item.state ?? item.toState ?? '';

  // Check if it's an action type (not a status)
  if (ACTION_LABELS[state]) {
    return ACTION_LABELS[state];
  }

  // For status transitions, try from/to mapping
  if (item.fromState && item.toState) {
    const key = `${item.fromState}-${item.toState}`;
    if (STATUS_ACTIONS[key]) return STATUS_ACTIONS[key];
  }

  // Fallback: display the state as-is
  return state ? `changed to ${state}` : 'changed';
}

const STATUS_STATES = new Set([
  'open', 'submitted', 'approved', 'rejected', 'cancelled', 'reimbursed', 'sent',
]);

function getDotColor(item: StateChangeItem): string {
  const state = item.state ?? item.toState ?? '';
  if (state === 'approved' || state === 'restored') return 'bg-green-500';
  if (state === 'rejected') return 'bg-red-500';
  if (state === 'submitted') return 'bg-yellow-500';
  if (state === 'archived' || state === 'deleted' || state.startsWith('assignment_deleted')) return 'bg-red-400';
  if (state === 'updated' || state.startsWith('assignment_')) return 'bg-blue-400';
  if (state === 'created') return 'bg-green-400';
  if (state === 'overage_approved') return 'bg-green-400';
  if (state === 'overage_acknowledged') return 'bg-blue-400';
  // External tasks v1 (260702) bod 3 — external reviewer decision.
  if (state === 'external_approved') return 'bg-green-500';
  if (state === 'external_rejected') return 'bg-red-500';
  if (state === 'author_approved') return 'bg-green-500';
  if (state === 'author_reopened') return 'bg-red-500';
  if (state === 'discount_override_applied') return 'bg-blue-400';
  return 'bg-gray-400';
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '(empty)';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export function StateHistory({ history, isLoading, hasMore, onLoadMore }: StateHistoryProps) {
  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex gap-4">
            <div className="w-2 h-2 bg-gray-200 rounded-full mt-2" />
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4" />
              <div className="h-3 bg-gray-200 rounded w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <p className="text-gray-500 text-sm">No history available.</p>
    );
  }

  return (
    <div className="flow-root">
      <ul className="-mb-8">
        {history.map((item, index) => {
          const state = item.state ?? item.toState ?? '';
          const showStatusBadge = STATUS_STATES.has(state);

          return (
            <li key={item.id}>
              <div className="relative pb-8">
                {(index !== history.length - 1 || hasMore) && (
                  <span
                    className="absolute top-4 left-2 -ml-px h-full w-0.5 bg-gray-200"
                    aria-hidden="true"
                  />
                )}
                <div className="relative flex items-start space-x-3">
                  {/* Timeline dot */}
                  <div className="relative">
                    <span className={`h-4 w-4 rounded-full flex items-center justify-center ring-4 ring-white ${getDotColor(item)}`}>
                      {state === 'approved' && (
                        <svg className="h-2.5 w-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                      {state === 'rejected' && (
                        <svg className="h-2.5 w-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      )}
                    </span>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-gray-900">
                      <span className="font-medium">{getUserName(item.user)}</span>
                      {' '}
                      <span className="text-gray-600">{getActionText(item)}</span>
                    </div>
                    <div className="mt-0.5 text-xs text-gray-500">
                      {item.createdAt ? formatDisplayDateTime(item.createdAt) : ''}
                    </div>
                    {item.reason && (
                      state.startsWith('assignment_') ? (
                        <div className="mt-0.5 text-xs text-gray-500">{item.reason}</div>
                      ) : (
                        <div className="mt-2 p-2 bg-gray-50 rounded-lg text-sm text-gray-600 border border-gray-200">
                          <span className="font-medium text-gray-700">Reason:</span> {item.reason}
                        </div>
                      )
                    )}
                    {item.changes && item.changes.length > 0 && (() => {
                      const directChanges = item.changes.filter(c => !c.field.startsWith('assignment:'));
                      const assignmentChanges = item.changes.filter(c => c.field.startsWith('assignment:'));
                      return (
                        <div className="mt-2 text-xs text-gray-600 space-y-1">
                          {directChanges.map((change, ci) => (
                            <div key={ci} className="flex flex-wrap gap-1">
                              <span className="font-medium text-gray-700">{change.field}:</span>
                              <span className="text-red-600 line-through">{formatValue(change.oldValue)}</span>
                              <span className="text-gray-400">&rarr;</span>
                              <span className="text-green-700">{formatValue(change.newValue)}</span>
                            </div>
                          ))}
                          {assignmentChanges.length > 0 && (
                            <div className="mt-2 ml-2 pl-3 border-l-2 border-gray-200 space-y-1.5">
                              <div className="text-xs font-medium text-gray-500">Updated assignments:</div>
                              {assignmentChanges.map((change, ci) => (
                                <div key={ci} className="flex flex-wrap gap-1 text-xs">
                                  <span className="text-gray-700">{change.field.replace('assignment:', '')}</span>
                                  <span className="text-red-600 line-through">{formatValue(change.oldValue)}</span>
                                  <span className="text-gray-400">&rarr;</span>
                                  <span className="text-green-700">{formatValue(change.newValue)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    {showStatusBadge && (
                      <div className="mt-2">
                        <StatusBadge status={state as TimesheetStatus} size="sm" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      {hasMore && onLoadMore && (
        <div className="mt-4 text-center">
          <button
            onClick={onLoadMore}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            Load more history...
          </button>
        </div>
      )}
    </div>
  );
}
